import { formatEur, type PropertyDetailDto } from "@gladis/shared";
import type { ReactNode } from "react";
import { useT } from "@gladis/ui/i18n";

/**
 * What the property IS, as opposed to what it is worth.
 *
 * ==========================================================================================
 * IT WAS NOWHERE
 * ==========================================================================================
 *
 * The detail page showed three money figures, a mortgage table and a valuation history — and not
 * one fact about the building. The address was in the page title and everything else the household
 * had typed on the way in (type, build year, floor area, energy label, what they paid and when) was
 * write-only: entered once at creation and never displayed again.
 *
 * That is the sort of gap nobody reports as a bug, because the page looks complete. It surfaces as a
 * member opening the edit form to remind themselves what the energy label was.
 *
 * ==========================================================================================
 * PURCHASE PRICE AND DATE ARE HERE, NOT WITH THE MONEY
 * ==========================================================================================
 *
 * "Paid" already appears as a figure at the top of the page, where it belongs — it is one side of
 * the gain. Repeated here it reads as a fact about the transaction rather than a term in a sum,
 * which is what makes the DATE meaningful: a house bought in 2019 and one bought last month tell
 * you very different things about the same gain.
 *
 * ==========================================================================================
 * THE REGION IS SHOWN, AND IT IS NOT DECORATION
 * ==========================================================================================
 *
 * `cbsRegionCode` decides whether the quarterly estimate runs at all. When it is absent the "Update
 * estimate" action is disabled and nothing explains why — so the fact appears here, in the member's
 * words, rather than being left as an inference from a greyed-out menu item.
 */
export interface PropertyFactsProps {
  property: PropertyDetailDto["property"];
}

export function PropertyFacts({ property }: PropertyFactsProps): ReactNode {
  const t = useT();
  const address = property.address;

  return (
    <dl className="grid grid-cols-1 gap-x-8 gap-y-4 sm:grid-cols-2 lg:grid-cols-3">
      <Fact label={t("prop.facts.addressLabel")}>
        {address.street} {address.houseNumber}
        {address.houseAddition ?? ""}
        <br />
        {address.postcode} {address.city}
        {address.countryCode !== "NL" && <> · {address.countryCode}</>}
      </Fact>

      <Fact label={t("prop.facts.type")}>
        {property.propertyType === "house" ? t("prop.facts.house") : t("prop.facts.apartment")}
      </Fact>
      <Fact label={t("prop.facts.built")}>{property.buildYear}</Fact>

      <Fact label={t("prop.facts.floorArea")}>
        {property.innerSurfaceAreaM2 === null ? "—" : `${String(property.innerSurfaceAreaM2)} m²`}
      </Fact>
      <Fact label={t("prop.facts.plot")}>
        {property.outerSurfaceAreaM2 === null ? "—" : `${String(property.outerSurfaceAreaM2)} m²`}
      </Fact>
      <Fact label={t("prop.facts.energyLabel")}>{property.energyLabel ?? "—"}</Fact>

      <Fact label={t("prop.facts.bought")}>
        {formatEur(property.purchasePrice)}
        <span className="text-muted"> · {property.purchaseDate}</span>
      </Fact>

      <Fact label={t("prop.facts.priceIndex")}>
        {property.indexable ? (
          t("prop.facts.tracked")
        ) : address.countryCode === "NL" ? (
          /*
           * Distinguishes the two reasons the estimate is unavailable, because only one of them is
           * fixable. An unresolvable address is usually a mistyped postcode.
           */
          <span className="text-muted">{t("prop.facts.addressUnrecognised")}</span>
        ) : (
          <span className="text-muted">{t("prop.facts.netherlandsOnly")}</span>
        )}
      </Fact>
    </dl>
  );
}

function Fact({ label, children }: { label: string; children: ReactNode }): ReactNode {
  return (
    <div>
      <dt className="text-muted text-sm">{label}</dt>
      <dd className="mt-0.5 font-medium">{children}</dd>
    </div>
  );
}
